/**
 * The CPU's squad.
 *
 * Drafted through exactly the same engine the user drafts through — same
 * feasibility guard, same two phases, same pool. A hand-built opponent XI would
 * drift out of the rules the moment either changed, and the calibration work
 * was done against real squads for the same reason: a hand-rolled XI measured
 * 162 where real squads measured 167.
 */

import {
  createDraft,
  evaluateSquad,
  pickInto,
  isComplete,
  createRng,
  type DraftState,
} from '@ipl-sim/engine-cricket';
import type { Card, Roster } from './roster';

export interface Opponent {
  readonly name: string;
  readonly xi: readonly Card[];
  readonly subs: readonly Card[];
}

/**
 * Neutral labels, matching the IP posture used for squads: real player names,
 * no franchise names, no logos.
 */
const NAMES = [
  'City XI',
  'The Invitational',
  'Blue Selects',
  'Coastal XI',
  'The Old Guard',
  'Northern Select',
  'The Challengers',
  'Union XI',
];

/**
 * Drafts a full opponent by taking a random legal offer each spin.
 *
 * Leaving a dead squad is free here for the same reason it is free for the
 * user: the guard promises a completion exists in the pool, not on the squad
 * in hand. A budget here would be a CPU that sometimes cannot field a side.
 */
export function draftOpponent(roster: Roster, from: number, to: number, seed: string): Opponent {
  const rng = createRng(seed);
  const teams = roster.squadsIn(from, to);
  const pool = roster.poolIn(from, to);

  let state: DraftState = createDraft();
  let spins = 0;

  while (!isComplete(state)) {
    if (++spins > 400) throw new Error(`CPU draft ${seed} never completed`);

    const offers = evaluateSquad(state, rng.pick(teams).squad, pool);
    const usable = offers.filter((o) => o.legalSlots.length > 0 || o.canBeImpact);
    if (usable.length === 0) continue;

    const offer = rng.pick(usable);
    state = offer.canBeImpact
      ? pickInto(state, offer.player, { kind: 'IMPACT' })
      : pickInto(state, offer.player, { kind: 'XI', slot: rng.pick(offer.legalSlots) });
  }

  // Batting order is the map key, so the XI must be read in slot order rather
  // than insertion order — the draft fills slots in whatever order it liked.
  const xi: Card[] = [];
  for (let slot = 1; slot <= 11; slot++) xi.push(state.xi.get(slot) as Card);

  return { name: rng.pick(NAMES), xi, subs: state.impactSubs as readonly Card[] };
}
